import React, { useState, useEffect, useCallback } from 'react';
import { HERO_SLIDES } from './constants';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from './i18n/LanguageContext';

export const HeroSlider: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t } = useLanguage();

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % HERO_SLIDES.length);
  }, [isAnimating]);

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 700); // Match transition duration
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // Auto-play
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Slides */}
      {HERO_SLIDES.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute top-0 left-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
            index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover opacity-80"
            />
            {/* Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
          </div>

          {/* Text Content - Floating above image */}
          <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4 z-20">
            <h2 
              className={`text-sm md:text-lg uppercase tracking-[0.3em] mb-4 transition-all duration-1000 delay-300 transform ${
                index === currentIndex ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              } ${slide.theme === 'light' ? 'text-gray-100' : 'text-gray-900'}`}
            >
              {t.hero.slides[index]?.subtitle || slide.subtitle}
            </h2>
            <h1 
              className={`text-5xl md:text-8xl font-display font-bold tracking-wider mb-8 transition-all duration-1000 delay-100 transform ${
                index === currentIndex ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              } ${slide.theme === 'light' ? 'text-white' : 'text-black'}`}
            >
              {t.hero.slides[index]?.title || slide.title}
            </h1>
          </div>
        </div>
      ))}

      {/* Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full border border-white/20 text-white/50 hover:bg-white/10 hover:text-white transition-all hidden md:block"
      >
        <ChevronLeft size={32} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full border border-white/20 text-white/50 hover:bg-white/10 hover:text-white transition-all hidden md:block"
      >
        <ChevronRight size={32} />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex space-x-4">
        {HERO_SLIDES.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsAnimating(true);
              setCurrentIndex(index);
            }}
            className={`h-1 transition-all duration-500 rounded-full ${
              index === currentIndex ? 'w-12 bg-white' : 'w-4 bg-white/30 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
